import { NgModule } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { UTextareaModule, UTieredMenuModule } from '@local/ui-infra';
import { LocalActionsRegistrator } from '@shared/services/local-actions-registrator';
import { InputAutocompletePipe } from './components/suggestions-dropdown/input-autocomplete.pipe';
import { ErrorGuard } from './routes/error.route-guard';
import { NavTreeNodeIdResolver } from './routes/nav-tree-node-id.resolver';
import { NavTreeNodeTitleResolver } from './routes/nav-tree-node-title.resolver';
import { NavTreeNodeResolver } from './routes/nav-tree-node.resolver';
import { WebSearchResolver } from './routes/web-search.resolver';
import { CollectionsService } from './services/collections.service';
import { CommandBarService } from './services/command-bar.service';
import { ResultCommandService } from './services/commands/result-command.service';
import { FiltersService } from './services/filters.service';
import { GoLinksService } from './services/go-links.service';
import { GoToService } from './services/go-to.service';
import { HubService } from './services/hub.service';
import { NavTreeService } from './services/nav-tree.service';
import { OpenWithPromptService } from './services/open-with-prompt.service';
import { ParamsSelectorService } from './services/params-selector.service';
import { ResultsService } from './services/results.service';
import { SearchService } from './services/search';
import { SearchClientFactoryService } from './services/search/search-client-factory-service';
import { SidebarService } from './services/sidebar.service';
import { SuggestionsSearchService } from './services/suggestions-search.service';
import { CollectionsPageModule } from './views/collections-page/collections-page.module';
import { HomePageModule } from './views/home-page/home-page.module';
/* Components */
import * as hubComponents from './views/hub';
import * as specialViewsComponents from './views/special-views';

import { ActionsComponent } from './views/calendar-page/actions/actions.component';
import { UpNextResultItemComponent } from './views/calendar-page/up-next/up-next-result-item.component';
import { UpNextComponent } from './views/calendar-page/up-next/up-next.component';
import { AvatarPopupComponent } from './views/hub/components/avatar-popup/avatar-popup.component';
import { OptionsPopupComponent } from './views/hub/components/launcher-avatar-popup/options-popup.component';
import { MinimizedSidebarComponent } from './views/hub/components/minimized-sidebar/minimized-sidebar.component';
import { ConnectAppsComponent } from './views/hub/connect-apps/connect-apps.component';
import { LauncherConnectAppsComponent } from './views/hub/connect-apps/launcher-connect-apps/launcher-connect-apps.component';
import { NavItemComponent } from './views/hub/shared/sidebar/nav-menu/nav-item.component';
import { FilePreviewModule } from './views/preview/file-preview/file-preview.module';
import { MailPreviewModule } from './views/preview/mail-preview/mail-preview.module';
import { PeoplePreviewModule } from './views/preview/people-preview/people-preview.module';
import { GoLinksViewModule } from './views/results-views/go-links/go-links-view.module';
import { MailBuildResultViewService } from './views/results-views/mail/mail-build-result-view.service';
import { ResultsComponent } from './views/results-views/results/results.component';
import { ErrorItemDialog } from './views/results/components/error-item/error-item-dialog.component';
import { RecentSearchesService } from './views/results/recent-searches.service';
/* Modules */
import { JsonPipe } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TreeModule } from '@odymaui/angular-tree-component';
import { ResultFiltersFocusService } from '@shared/components/filters/result-filters-focus.service';
import { OpenWithPromptComponent } from '@shared/components/prompt/open-with-prompt.component';
import { AssistantsService } from '@shared/services/assistants.service';
import { InstructionsOverlayComponent } from './components/instructions-overlay/instructions-overlay.component';
import { SharedBarModule } from './components/shared-bar.module';
import { SuggestResyncOverlayComponent } from './components/suggest-resync-overlay/suggest-resync-overlay.component';
import { SuggestionsAutocompleteComponent } from './components/suggestions-autocomplete/suggestions-autocomplete.component';
import { SuggestionsDropdownItemComponent } from './components/suggestions-dropdown/suggestions-dropdown-item.component';
import { SuggestionsDropdownComponent } from './components/suggestions-dropdown/suggestions-dropdown.component';
import { WalkthroughGalleryComponent } from './components/walkthrough-gallery/walkthrough-gallery.component';
import { ClickTrackLocationDirective } from './directives/track-location.directive';
import { MovingDataPopupHubModule } from './modules/moving-data-popup-hub.module';
import { CanActivateChatGuard } from './routes/guards/can-activate-chat.guard';
import { CanActivateCollectionsGuard } from './routes/guards/can-activate-collection.guard';
import { CanActivateFavoritesGuard } from './routes/guards/can-activate-favorites.guard';
import { CanActivateGoLinksGuard } from './routes/guards/can-activate-go-link.guard';
import { CanActivateWikiGuard } from './routes/guards/can-activate-wiki.guard';
import { CanDeactivateCollectionGuard } from './routes/guards/can-deactivate-collection.guard';
import { MainRoutingModule } from './routes/main-routing.module';
import { FyiService } from './services';
import { CollectionsUtilService } from './services/collections-util.service';
import { ExperienceErrorsService } from './services/experience-errors.service';
import { ExperiencesService } from './services/experiences.service';
import { HelpPopupService } from './services/help-popup.service';
import { HomePageService } from './services/home-page.service';
import { IdentitiesSuggestionsService } from './services/identities-suggestions.service';
import { PreviewKeyboardService } from './services/preview-keyboard.service';
import { QuestionnaireService } from './services/questionnaire.service';
import { QuickLinksService } from './services/quick-links.service';
import { ResultMarkdownService } from './services/result-markdown.service';
import { SearchParamsService } from './services/search-params.service';
import { ShareOptionsService } from './services/share-options.service';
import { SortService } from './services/sort.service';
import { UrlPreviewService } from './services/url-preview.service';
import { UrlResolverService } from './services/url-resolver.service';
import { WikiCardBuilderService } from './services/wikis/wiki-cards-builder.service';
import { WikiCardsVerificationsService } from './services/wikis/wiki-cards-verifications.service';
import { WikiCardsService } from './services/wikis/wiki-cards.service';
import { WikiDraftsService } from './services/wikis/wiki-drafts.service';
import { WikiFoldersService } from './services/wikis/wiki-folders.service';
import { WikisService } from './services/wikis/wikis.service';
import { AssistantIncontextModule } from './views/assistant-incontext/assistant-incontext.module';
import { ChatPageModule } from './views/chat-page/chat-page.module';
import { CollectionResolver } from './views/collections-page/services/collection.resolver';
import { AppTitleBarComponent } from './views/hub/components/app-title-bar/app-title-bar.component';
import { CardPreviewModule } from './views/preview/card-preview/card-preview.module';
import { QuestionnaireModule } from './views/results-views/questionnaire/questionnaire.module';
import { RelevantPeopleStateComponent } from './views/results-views/relevant-people/relevant-people-empty-state/relevant-people-empty-state.component';
import { InlineReferenceItemModule } from './views/results/components/inline-reference-item/inline-reference-item.module';
import { WebSearchModule } from './views/web-search/web-search.module';
import { WikiCardVersionsService } from './services/wikis/wiki-card-versions.service';
import { BlobsStorageService } from './services/blobs/blobs-storage.service';
import { ItemsCache } from './services/items-cache';
import { ItemsCacheService } from './services/items-cache.service';
import { UploadBlobService } from './services/blobs/upload-blob.service';
import { BlobContextMenuService } from './services/blobs/blob-context-menu.service';
import { SidePanelModule } from './views/side-panel/side-panel.module';
import { SearchPopUpService } from './views/special-views/search-popup/search-popup.service';
import { ContextMenuItemsService } from './services/commands/context-menu-items.service';
import { SaveFiltersService } from './services/save-filters.service';
import { WritingAssistantService } from './services/writing-assistant.service';
import { SaveFiltersGuard } from './routes/guards/can-activate-save-filters.guard';
import { PopupSettingsService } from './views/settings/service/popup-service-settings.service';

const libUiModules = [TreeModule, UTextareaModule, UTieredMenuModule];

const materialModules = [MatDialogModule, MatProgressBarModule];

const appModules = [
  AssistantIncontextModule,
  CardPreviewModule,
  CollectionsPageModule,
  FilePreviewModule,
  GoLinksViewModule,
  HomePageModule,
  MailPreviewModule,
  PeoplePreviewModule,
  WebSearchModule,
  QuestionnaireModule,
  ChatPageModule,
  InlineReferenceItemModule,
  SidePanelModule,
];
@NgModule({
  imports: [MainRoutingModule, SharedBarModule, ...materialModules, ...libUiModules, ...appModules, MovingDataPopupHubModule.forRoot()],
  providers: [
    GoToService,
    SuggestionsSearchService,
    InputAutocompletePipe,
    HubService,
    CollectionsService,
    CollectionsUtilService,
    WikiCardsService,
    WikiDraftsService,
    WikiCardVersionsService,
    WikiCardBuilderService,
    WikisService,
    WikiFoldersService,
    SearchService,
    SearchClientFactoryService,
    SidebarService,
    HelpPopupService,
    HomePageService,
    CommandBarService,
    NavTreeService,
    LocalActionsRegistrator,
    NavTreeNodeResolver,
    NavTreeNodeIdResolver,
    WebSearchResolver,
    ErrorGuard,
    FiltersService,
    ParamsSelectorService,
    ResultCommandService,
    NavTreeNodeTitleResolver,
    GoLinksService,
    ResultMarkdownService,
    ResultsService,
    PreviewKeyboardService,
    RecentSearchesService,
    OpenWithPromptService,
    SearchParamsService,
    ExperiencesService,
    IdentitiesSuggestionsService,
    AssistantsService,
    MailBuildResultViewService,
    UrlResolverService,
    UrlPreviewService,
    SortService,
    QuickLinksService,
    ResultFiltersFocusService,
    CollectionResolver,
    JsonPipe,
    WikiCardsVerificationsService,
    CanDeactivateCollectionGuard,
    CanActivateWikiGuard,
    CanActivateCollectionsGuard,
    CanActivateFavoritesGuard,
    CanActivateGoLinksGuard,
    SaveFiltersGuard,
    CanActivateChatGuard,
    FyiService,
    ShareOptionsService,
    QuestionnaireService,
    ExperienceErrorsService,
    SearchPopUpService,
    BlobsStorageService,
    UploadBlobService,
    BlobContextMenuService,
    ContextMenuItemsService,
    ItemsCache,
    ItemsCacheService,
    SaveFiltersService,
    WritingAssistantService,
    PopupSettingsService,
  ],
  declarations: [
    ActionsComponent,
    AvatarPopupComponent,
    ClickTrackLocationDirective,
    ConnectAppsComponent,
    ErrorItemDialog,
    hubComponents.FyiDropdownComponent,
    hubComponents.FyiDropdownItemComponent,
    hubComponents.FyiPopupComponent,
    hubComponents.FyiEmptyStateComponent,
    hubComponents.HubComponent,
    hubComponents.NavMenuComponent,
    hubComponents.SidebarComponent,
    InputAutocompletePipe,
    InstructionsOverlayComponent,
    LauncherConnectAppsComponent,
    MinimizedSidebarComponent,
    NavItemComponent,
    OpenWithPromptComponent,
    OptionsPopupComponent,
    ResultsComponent,
    SuggestionsAutocompleteComponent,
    SuggestionsDropdownComponent,
    SuggestionsDropdownItemComponent,
    SuggestResyncOverlayComponent,
    UpNextComponent,
    UpNextResultItemComponent,
    WalkthroughGalleryComponent,
    RelevantPeopleStateComponent,
    AppTitleBarComponent,
    specialViewsComponents.ErrorPageComponent,
    specialViewsComponents.SearchPopupComponent,
    specialViewsComponents.SearchPopupItemComponent,
  ],
})
export class MainModule {}
